import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import VideoCard from './VideoCard';
import { liveAPI } from '../api/client';
import { isRTL } from '../utils/translations';
import { Radio, Users, Sparkles } from 'lucide-react';

const VideoFeed = ({ videos, title, subtitle, showViewAll = true, onAddToCart, onBuyNow }) => {
    const [activeLiveRooms, setActiveLiveRooms] = useState([]);

    useEffect(() => {
        let isMounted = true;
        liveAPI.getActiveRooms()
            .then(rooms => {
                if (isMounted && Array.isArray(rooms)) {
                    setActiveLiveRooms(rooms);
                }
            })
            .catch(() => {});

        return () => { isMounted = false; };
    }, []);

    if (!videos || videos.length === 0) return null;

    const primaryLive = activeLiveRooms.length > 0 ? activeLiveRooms[0] : null;

    return (
        <section className="section container video-commerce-section" style={{ position: 'relative' }}>
            <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <div className="section-kicker" style={{ color: '#000000', fontWeight: '800', letterSpacing: '1px' }}>
                        {isRTL() ? 'تسوق الفيديو المباشر' : 'VIDEO COMMERCE'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <h2 className="section-title" style={{ margin: 0, fontWeight: '800' }}>{title || 'VIDEO COMMERCE'}</h2>
                        {primaryLive && (
                            <Link 
                                to={`/live-room/${primaryLive.id}`}
                                className="live-feed-corner-pill"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '4px 12px',
                                    borderRadius: '20px',
                                    background: '#000000',
                                    border: '1.5px solid #FE2C55',
                                    color: '#FFFFFF',
                                    fontSize: '11px',
                                    fontWeight: '800',
                                    textDecoration: 'none',
                                    boxShadow: '0 0 14px rgba(254, 44, 85, 0.4)',
                                    animation: 'pulseLiveDot 1.6s infinite'
                                }}
                            >
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FE2C55', display: 'inline-block' }} />
                                <span>{isRTL() ? '🔴 مباشر الآن' : '🔴 LIVE NOW'}</span>
                            </Link>
                        )}
                    </div>
                    {subtitle ? <p className="section-subtitle" style={{ margin: '6px 0 0 0', color: '#64748B' }}>{subtitle}</p> : null}
                    <div className="section-accent" style={{ background: '#000000' }} />
                </div>
                {showViewAll && (
                    <Link 
                        to="/videos" 
                        className="section-link"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: '#000000',
                            color: '#FFFFFF',
                            padding: '8px 18px',
                            borderRadius: '10px',
                            fontSize: '13px',
                            fontWeight: '700',
                            textDecoration: 'none'
                        }}
                    >
                        <span>📱</span>
                        <span>{isRTL() ? 'تقليب تيك توك ←' : 'WATCH REELS →'}</span>
                    </Link>
                )}
            </div>

            <div className="video-grid">
                {/* Interspersed Live Stream Card if Live is Active */}
                {primaryLive && (
                    <div 
                        className="video-card video-card--grid live-interspersed-card"
                        style={{
                            background: '#0D0E15',
                            borderRadius: '20px',
                            border: '2px solid #FE2C55',
                            padding: '14px',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 10px 32px rgba(254,44,85,0.3)',
                            position: 'relative'
                        }}
                    >
                        <div 
                            style={{
                                position: 'relative',
                                width: '100%',
                                height: '360px',
                                maxHeight: '380px',
                                borderRadius: '14px',
                                overflow: 'hidden',
                                background: '#000000',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '14px',
                                boxSizing: 'border-box'
                            }}
                        >
                            {/* Live Badge in Corner */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 2 }}>
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    background: '#FE2C55',
                                    color: '#FFFFFF',
                                    padding: '4px 10px',
                                    borderRadius: '16px',
                                    fontSize: '11px',
                                    fontWeight: '800',
                                    boxShadow: '0 0 10px rgba(254,44,85,0.6)'
                                }}>
                                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#FFFFFF' }} />
                                    <span>{isRTL() ? 'مباشر الآن' : 'LIVE'}</span>
                                </span>
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    background: 'rgba(0,0,0,0.6)',
                                    backdropFilter: 'blur(6px)',
                                    color: '#FFFFFF',
                                    padding: '3px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '700'
                                }}>
                                    <Users size={12} />
                                    <span>{primaryLive.viewersCount || 1}</span>
                                </span>
                            </div>

                            {/* Center Pulse Icon */}
                            <div style={{ textAlign: 'center', zIndex: 2 }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    background: 'rgba(254,44,85,0.2)',
                                    border: '2px solid #FE2C55',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#FFFFFF',
                                    boxShadow: '0 0 25px rgba(254,44,85,0.5)',
                                    animation: 'pulseDot 1.4s infinite'
                                }}>
                                    <Radio size={32} color="#FE2C55" />
                                </div>
                            </div>

                            {/* Bottom Info Overlay */}
                            <div style={{ zIndex: 2 }}>
                                <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: '700', marginBottom: '2px' }}>
                                    {primaryLive.hostName || (isRTL() ? 'إدارة لوكسفونو الرسمية' : 'LuxVono Official')}
                                </div>
                                <div style={{ fontSize: '15px', color: '#FFFFFF', fontWeight: '800', lineHeight: '1.3', marginBottom: '10px' }}>
                                    {primaryLive.title || (isRTL() ? 'بث العروض والمقتنيات الفاخرة' : 'Live Showcase')}
                                </div>
                                <Link 
                                    to={`/live-room/${primaryLive.id}`}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'center',
                                        padding: '10px 0',
                                        borderRadius: '10px',
                                        background: '#FE2C55',
                                        color: '#FFFFFF',
                                        fontWeight: '800',
                                        fontSize: '13px',
                                        textDecoration: 'none',
                                        boxShadow: '0 4px 14px rgba(254,44,85,0.4)',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    {isRTL() ? 'انضم للبث المباشر ➔' : 'Join Live Stream ➔'}
                                </Link>
                            </div>
                        </div>
                    </div>
                )}

                {videos.map(video => (
                    <VideoCard key={video.id} video={video} onAddToCart={onAddToCart} onBuyNow={onBuyNow} />
                ))}
            </div>
        </section>
    );
};

export default VideoFeed;
